import { Modal, ModalType, Option, Table, TableRow } from '@code/innovance-react-ui-kit';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import { TableWrapper } from 'src/front/components/FormStyles';
import { IdentityFormData } from 'src/front/components/identity/IdentityFormData';
import IdentityModal from 'src/front/components/identity/IdentityModal';
import uid from 'uid';

const baseHeaders = [
  'general_documentType_label',
  'general_documentSeries_label',
  'general_documentNumber_label',
  'general_issueDate_label',
  'general_issuedBy_label',
  'general_expiredDate_label',
  'cardEntry_documentEntry_unitCode_label',
];

interface Props {
  identities: Identity[];
  setIdentities: Dispatch<SetStateAction<Identity[]>>;
  identityTypes: Option[];
  isLoading: boolean;
  isEditable?: boolean;
  disablePage?: boolean;
  showActiveFlag?: boolean;
}

interface Identity extends IdentityFormData {
  isNew?: boolean;
}

const IdentityEntry: FC<Props> = ({
  identities,
  isLoading,
  identityTypes,
  setIdentities,
  isEditable = false,
  disablePage,
  showActiveFlag,
}) => {
  const { t } = useTranslation();
  const [showIdentityModal, setShowIdentityModal] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const [identityFormValues, setIdentityFormValues] = useState<Identity>();
  const [selectedIdentity, setSelectedIdentity] = useState<TableRow>();
  const [isNew, setIsNew] = useState<boolean>();

  const identityTableHeaders = showActiveFlag
    ? [...baseHeaders, 'changeFundClientInfo_activityFlag_text']
    : baseHeaders;
  const widths = showActiveFlag
    ? ['22', '9', '14', '14', '11', '11', '12', '7']
    : ['23', '10', '15', '15', '12', '12', '13'];

  useEffect((): void => {
    if (identityTypes.length > 0) {
      !isLoading && setShowIdentityModal(identities.length < 1);
    }
  }, [identities.length, identityTypes.length, isLoading]);

  const closeIdentityModal = (): void => setShowIdentityModal(false);

  const handleIdentitySubmit = (identity: Identity): void => {
    let newIdentities;
    if (identity?.id) {
      newIdentities = identities.map((idt) => (idt.id === identity.id ? identity : idt));
    } else {
      newIdentities = [{ ...identity, id: uid(), isNew: true }, ...identities];
    }
    setIdentities(newIdentities);
    setShowIdentityModal(false);
  };

  const handleDeleteIdentity = (): void => {
    setIdentities(identities.filter((idt) => idt.id !== selectedIdentity?.id));
    setSelectedIdentity(undefined);
    setIsDeleteModalOpen(false);
  };

  const addIdentity = useCallback((): void => {
    setIdentityFormValues(undefined);
    setShowIdentityModal(true);
    setIsNew(true);
  }, []);

  const editIdentity = useCallback(
    (item?: TableRow): void => {
      const identity = identities.find((idt) => idt.id === item?.id);
      setIdentityFormValues(identity);
      setShowIdentityModal(true);
      setIsNew(identity?.isNew ?? false);
    },
    [identities]
  );

  const deleteIdentity = useCallback((item?: TableRow): void => {
    setIsDeleteModalOpen(true);
  }, []);

  const identityActions = useMemo(
    () => ({
      add: addIdentity,
      edit: editIdentity,
      delete: deleteIdentity,
    }),
    [addIdentity, deleteIdentity, editIdentity]
  );

  const identityTableData: TableRow[] = identities.map((identity) => ({
    id: identity.id || '',
    columns: [
      identity.identityType?.label || '',
      identity.identityDocumentSerialCode,
      identity.identityDocumentSerialNumber,
      formatDate(identity.issueDate),
      identity.issuedBy,
      formatDate(identity.expireDate),
      identity.unitCode,
      ...(showActiveFlag ? [identity.isActive ? '1' : '0'] : []),
    ],
    deletable: !!identity.isNew,
    editable: isEditable || !!identity.isNew,
  }));

  return (
    <>
      {showIdentityModal && (
        <IdentityModal
          formValues={identityFormValues}
          identityTypes={identityTypes}
          isNew={isNew}
          onClose={closeIdentityModal}
          showActiveFlag={showActiveFlag}
          submit={handleIdentitySubmit}
        />
      )}
      {isDeleteModalOpen && (
        <Modal
          cancelText={t('general_cancel_button')}
          description={t('general_deleteRecordConfirm_text')}
          onCancel={() => setIsDeleteModalOpen((state) => !state)}
          onSubmit={handleDeleteIdentity}
          submitText={t('general_components_confirm_button')}
          title="Confirmation"
          type={ModalType.Confirmation}
        />
      )}
      <TableWrapper>
        <Table
          actionConfig={disablePage ? {} : identityActions}
          addActionText={t('general_add_button')}
          emptyTableText={t('cardEntry_documentEntry_noDocumentInfoFound_text')}
          headers={identityTableHeaders.map((head) => t(head))}
          items={identityTableData}
          loading={isLoading}
          onSelect={setSelectedIdentity}
          pageNumber={1}
          selectedItem={selectedIdentity}
          widths={widths}
        />
      </TableWrapper>
    </>
  );
};

export default IdentityEntry;
